import React from 'react';
import {CButton, CFormGroup, CInput, CInputCheckbox, CLabel, CSelect, CTextarea} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {
    Etapa,
    Fluxo,
    Grupo,
    Item,
    Opcao,
    RegraInicio,
    RegraInicioList,
    RegraObservacao,
    RegraObservacaoList,
    Selecao,
    TIPOS_SELECAO,
    TipoItem,
    TipoItemList,
    novaChave,
    ordenaPorGrupo,
} from "./modelo";

interface Props {
    fluxo: Fluxo,
    selecao: Selecao,
    somenteLeitura: boolean,
    onSelecionar: (selecao: Selecao | null) => void,
    /** Fecha o painel no celular (no desktop ele e uma coluna fixa). */
    onFechar: () => void,
    onMudarEtapas: (etapas: Etapa[]) => void,
}

const Campo = ({rotulo, obrigatorio, ajuda, children}: {rotulo: string, obrigatorio?: boolean, ajuda?: string, children: any}) => (
    <CFormGroup>
        <CLabel className={obrigatorio ? 'text-danger' : ''}>{rotulo}</CLabel>
        {children}
        {ajuda && <small className="form-text text-muted">{ajuda}</small>}
    </CFormGroup>
);

const Marcar = ({rotulo, valor, desligado, onMudar}: {rotulo: string, valor: boolean, desligado?: boolean, onMudar: (v: boolean) => void}) => {
    const idCampo = `marcar-${rotulo.replace(/\W+/g, '-')}`;
    return (
        <CFormGroup variant="checkbox" className="checkbox fx-marcar">
            <CInputCheckbox id={idCampo} checked={valor} disabled={desligado} onChange={(e: any) => onMudar(e.target.checked)}/>
            <CLabel variant="checkbox" className="form-check-label" htmlFor={idCampo}>{rotulo}</CLabel>
        </CFormGroup>
    );
};

const proximoCodigoGrupo = (etapa: Etapa, ordemEtapa: number) => {
    const usados = new Set(etapa.grupos.map(g => g.codigo));
    let n = etapa.grupos.length + 1;
    while (usados.has(`${ordemEtapa}.${n}`)) {
        n++;
    }
    return `${ordemEtapa}.${n}`;
};

/**
 * Painel de edicao do que esta selecionado na trilha: a etapa (com seus
 * grupos) ou o item. Em tela pequena ocupa a tela inteira (scss).
 */
const Painel = ({fluxo, selecao, somenteLeitura, onSelecionar, onFechar, onMudarEtapas}: Props) => {
    const indiceEtapa = fluxo.etapas.findIndex(e => e._k === selecao.etapa);
    const etapa = fluxo.etapas[indiceEtapa];
    const item = etapa && selecao.item ? etapa.itens.find(i => i._k === selecao.item) : undefined;
    if (!etapa || (selecao.item && !item)) {
        return null;
    }
    const ro = somenteLeitura;

    const trocaEtapa = (nova: Etapa) => onMudarEtapas(fluxo.etapas.map(e => (e._k === etapa._k ? nova : e)));
    const mudaEtapa = (campo: keyof Etapa, valor: any) => {
        const nova = {...etapa, [campo]: valor};
        if (campo === 'duracao_dias_uteis') {
            // Prazo do item nunca passa da duracao da etapa.
            const limite = Math.max(1, Number(valor) || 1);
            nova.itens = etapa.itens.map(i => (i.prazo_dias_uteis > limite ? {...i, prazo_dias_uteis: limite} : i));
        }
        trocaEtapa(nova);
    };
    const mudaItem = (campo: keyof Item, valor: any) => {
        const novo: Item = {...item!, [campo]: valor};
        if (campo === 'tipo') {
            if (TIPOS_SELECAO.includes(valor) && novo.opcoes.length === 0) {
                novo.opcoes = [{rotulo: '', exige_observacao: false}, {rotulo: '', exige_observacao: false}];
            }
            if (!TIPOS_SELECAO.includes(valor) && novo.observacao === RegraObservacao.CONFORME_OPCAO) {
                novo.observacao = RegraObservacao.OBRIGATORIA;
            }
            if (valor === TipoItem.ARQUIVO) {
                novo.exige_anexo = true;
            }
        }
        const nova = {...etapa, itens: etapa.itens.map(i => (i._k === item!._k ? novo : i))};
        trocaEtapa(campo === 'grupo' ? ordenaPorGrupo(nova) : nova);
    };

    // ---------------------------------------------------------------- grupos
    const mudaGrupo = (indice: number, campo: keyof Grupo, valor: string) => {
        const antigo = etapa.grupos[indice];
        const grupos = etapa.grupos.map((g, i) => (i === indice ? {...g, [campo]: valor} : g));
        let itens = etapa.itens;
        if (campo === 'codigo') {
            itens = itens.map(i => (i.grupo === antigo.codigo ? {...i, grupo: valor} : i));
        }
        trocaEtapa({...etapa, grupos, itens});
    };
    const adicionaGrupo = () => {
        trocaEtapa({...etapa, grupos: [...etapa.grupos, {codigo: proximoCodigoGrupo(etapa, indiceEtapa + 1), nome: ''}]});
    };
    const removeGrupo = (indice: number) => {
        const removido = etapa.grupos[indice];
        trocaEtapa(ordenaPorGrupo({
            ...etapa,
            grupos: etapa.grupos.filter((_, i) => i !== indice),
            itens: etapa.itens.map(i => (i.grupo === removido.codigo ? {...i, grupo: null} : i)),
        }));
    };
    const moveGrupo = (indice: number, direcao: -1 | 1) => {
        const grupos = [...etapa.grupos];
        const [g] = grupos.splice(indice, 1);
        grupos.splice(indice + direcao, 0, g);
        trocaEtapa(ordenaPorGrupo({...etapa, grupos}));
    };

    // ---------------------------------------------------------------- opcoes
    const mudaOpcao = (indice: number, campo: keyof Opcao, valor: any) => {
        mudaItem('opcoes', item!.opcoes.map((o, i) => (i === indice ? {...o, [campo]: valor} : o)));
    };

    const removeEtapa = () => {
        const quantos = etapa.itens.length;
        if (quantos && !window.confirm(`Excluir a etapa "${etapa.nome}" e os ${quantos} itens dela?`)) {
            return;
        }
        onMudarEtapas(fluxo.etapas.filter(e => e._k !== etapa._k));
        onSelecionar(null);
    };
    const removeItem = () => {
        trocaEtapa({...etapa, itens: etapa.itens.filter(i => i._k !== item!._k)});
        onSelecionar({etapa: etapa._k});
    };
    const duplicaItem = () => {
        const copia: Item = {...item!, id: undefined, _k: novaChave(), titulo: `${item!.titulo} (cópia)`, opcoes: item!.opcoes.map(o => ({...o}))};
        const itens = [...etapa.itens];
        itens.splice(itens.indexOf(item!) + 1, 0, copia);
        trocaEtapa({...etapa, itens});
        onSelecionar({etapa: etapa._k, item: copia._k});
    };

    const opcoesPapel = fluxo.papeis.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nome}</option>);

    return (
        <aside className="fx-painel">
            <div className="fx-painel-topo">
                <div className="fx-painel-titulo">
                    <span className="fx-painel-tipo">{item ? 'Item' : 'Propriedades da etapa'}</span>
                    <span className="fx-painel-contexto">{item ? `Etapa ${indiceEtapa + 1} · ${etapa.nome}` : `Etapa ${indiceEtapa + 1}`}</span>
                </div>
                {item && (
                    <CButton size="sm" color="secondary" variant="ghost" className="fx-so-desktop" title="Voltar às propriedades da etapa"
                             onClick={() => onSelecionar({etapa: etapa._k})}>
                        <CIcon name="cil-x"/>
                    </CButton>
                )}
                <CButton size="sm" color="secondary" variant="ghost" className="fx-so-celular" title="Fechar" onClick={onFechar}>
                    <CIcon name="cil-x"/>
                </CButton>
            </div>

            {!item && (
                <div className="fx-painel-corpo">
                    <Campo rotulo="Nome" obrigatorio>
                        <CInput value={etapa.nome} maxLength={120} disabled={ro} onChange={(e: any) => mudaEtapa('nome', e.target.value)}/>
                    </Campo>
                    <Campo rotulo="Descrição" ajuda="Aparece para o cliente no acompanhamento da implantação.">
                        <CTextarea rows={2} value={etapa.descricao || ''} disabled={ro} onChange={(e: any) => mudaEtapa('descricao', e.target.value)}/>
                    </Campo>
                    <div className="fx-linha2">
                        <Campo rotulo="Duração (dias úteis)" obrigatorio>
                            <CInput type="number" min={1} max={365} value={etapa.duracao_dias_uteis} disabled={ro}
                                    onChange={(e: any) => mudaEtapa('duracao_dias_uteis', Math.max(1, parseInt(e.target.value, 10) || 1))}/>
                        </Campo>
                        <Campo rotulo="Início">
                            <CSelect value={indiceEtapa === 0 ? RegraInicio.APOS_ANTERIOR : etapa.regra_inicio} disabled={ro || indiceEtapa === 0}
                                     onChange={(e: any) => mudaEtapa('regra_inicio', Number(e.target.value))}>
                                {RegraInicioList.getList().map((r: any) => <option key={r.value} value={r.value}>{r.description}</option>)}
                            </CSelect>
                        </Campo>
                    </div>
                    <Campo rotulo="Responsável pela etapa" obrigatorio>
                        <CSelect value={etapa.papel} disabled={ro} onChange={(e: any) => mudaEtapa('papel', e.target.value)}>
                            {opcoesPapel}
                        </CSelect>
                    </Campo>
                    <Marcar rotulo="Visível ao cliente" valor={etapa.visivel_cliente} desligado={ro} onMudar={v => mudaEtapa('visivel_cliente', v)}/>
                    <Marcar rotulo="Ativa (inativa não entra no processo)" valor={etapa.ativo} desligado={ro} onMudar={v => mudaEtapa('ativo', v)}/>

                    <div className="fx-painel-secao">
                        <strong>Grupos</strong>
                        <small className="text-muted"> agrupam os itens na tela (ex.: 1.2 Levantamento por módulo)</small>
                    </div>
                    {etapa.grupos.map((grupo, i) => (
                        <div className="fx-grupo-edicao" key={i}>
                            <CInput size="sm" className="codigo" value={grupo.codigo} maxLength={10} disabled={ro}
                                    onChange={(e: any) => mudaGrupo(i, 'codigo', e.target.value.trim())}/>
                            <CInput size="sm" value={grupo.nome} maxLength={120} disabled={ro} placeholder="Nome do grupo"
                                    onChange={(e: any) => mudaGrupo(i, 'nome', e.target.value)}/>
                            {!ro && (
                                <span className="fx-botoes">
                                    <CButton size="sm" variant="ghost" disabled={i === 0} onClick={() => moveGrupo(i, -1)} title="Subir"><CIcon name="cil-arrow-top"/></CButton>
                                    <CButton size="sm" variant="ghost" disabled={i === etapa.grupos.length - 1} onClick={() => moveGrupo(i, 1)} title="Descer"><CIcon name="cil-arrow-bottom"/></CButton>
                                    <CButton size="sm" variant="ghost" color="danger" onClick={() => removeGrupo(i)} title="Excluir grupo (os itens ficam sem grupo)"><CIcon name="cil-trash"/></CButton>
                                </span>
                            )}
                        </div>
                    ))}
                    {!ro && (
                        <>
                            <CButton size="sm" color="link" onClick={adicionaGrupo}><CIcon name="cil-plus"/> grupo</CButton>
                            <hr/>
                            <CButton size="sm" color="danger" variant="outline" onClick={removeEtapa}>
                                <CIcon name="cil-trash"/> Excluir etapa
                            </CButton>
                        </>
                    )}
                </div>
            )}

            {item && (
                <div className="fx-painel-corpo">
                    <Campo rotulo="Título (atividade)" obrigatorio>
                        <CTextarea rows={2} value={item.titulo} maxLength={200} disabled={ro} onChange={(e: any) => mudaItem('titulo', e.target.value)}/>
                    </Campo>
                    <Campo rotulo="Orientação de execução">
                        <CTextarea rows={3} value={item.orientacao || ''} disabled={ro} onChange={(e: any) => mudaItem('orientacao', e.target.value)}/>
                    </Campo>
                    <div className="fx-linha2">
                        <Campo rotulo="Grupo">
                            <CSelect value={item.grupo ?? ''} disabled={ro} onChange={(e: any) => mudaItem('grupo', e.target.value || null)}>
                                <option value="">Sem grupo</option>
                                {etapa.grupos.map(g => <option key={g.codigo} value={g.codigo}>{g.codigo} {g.nome}</option>)}
                            </CSelect>
                        </Campo>
                        <Campo rotulo="Módulo">
                            <CSelect value={item.modulo ?? ''} disabled={ro} onChange={(e: any) => mudaItem('modulo', e.target.value || null)}>
                                <option value="">Nenhum</option>
                                {fluxo.modulos.map(m => <option key={m.codigo} value={m.codigo}>{m.nome}{m.opcional ? ' (opcional)' : ''}</option>)}
                            </CSelect>
                        </Campo>
                    </div>
                    <div className="fx-linha2">
                        <Campo rotulo="Papel (quem executa)" obrigatorio>
                            <CSelect value={item.papel} disabled={ro} onChange={(e: any) => mudaItem('papel', e.target.value)}>
                                {opcoesPapel}
                            </CSelect>
                        </Campo>
                        <Campo rotulo="Prazo (d.u.)" obrigatorio ajuda={`De 1 a ${etapa.duracao_dias_uteis}, contados do início da etapa.`}>
                            <CInput type="number" min={1} max={etapa.duracao_dias_uteis} value={item.prazo_dias_uteis} disabled={ro}
                                    onChange={(e: any) => mudaItem('prazo_dias_uteis', Math.min(etapa.duracao_dias_uteis, Math.max(1, parseInt(e.target.value, 10) || 1)))}/>
                        </Campo>
                    </div>
                    <div className="fx-linha2">
                        <Campo rotulo="Tipo de resposta">
                            <CSelect value={item.tipo} disabled={ro} onChange={(e: any) => mudaItem('tipo', Number(e.target.value))}>
                                {TipoItemList.getList().map((t: any) => <option key={t.value} value={t.value}>{t.description}</option>)}
                            </CSelect>
                        </Campo>
                        <Campo rotulo="Observação">
                            <CSelect value={item.observacao} disabled={ro} onChange={(e: any) => mudaItem('observacao', Number(e.target.value))}>
                                {RegraObservacaoList.getList()
                                    .filter((r: any) => r.value !== RegraObservacao.CONFORME_OPCAO || TIPOS_SELECAO.includes(item.tipo))
                                    .map((r: any) => <option key={r.value} value={r.value}>{r.description}</option>)}
                            </CSelect>
                        </Campo>
                    </div>

                    {TIPOS_SELECAO.includes(item.tipo) && (
                        <div className="fx-opcoes">
                            <CLabel className="text-danger">Opções</CLabel>
                            {item.opcoes.map((opcao, i) => (
                                <div className="fx-opcao" key={i}>
                                    <CInput size="sm" value={opcao.rotulo} placeholder={`Opção ${i + 1}`} disabled={ro}
                                            onChange={(e: any) => mudaOpcao(i, 'rotulo', e.target.value)}/>
                                    {item.observacao === RegraObservacao.CONFORME_OPCAO && (
                                        <label className="fx-opcao-obs" title="Escolher esta opção exige observação">
                                            <input type="checkbox" checked={opcao.exige_observacao} disabled={ro}
                                                   onChange={e => mudaOpcao(i, 'exige_observacao', e.target.checked)}/> pede obs.
                                        </label>
                                    )}
                                    {!ro && (
                                        <CButton size="sm" variant="ghost" color="danger" title="Remover opção"
                                                 onClick={() => mudaItem('opcoes', item.opcoes.filter((_, j) => j !== i))}>
                                            <CIcon name="cil-x"/>
                                        </CButton>
                                    )}
                                </div>
                            ))}
                            {!ro && (
                                <CButton size="sm" color="link" onClick={() => mudaItem('opcoes', [...item.opcoes, {rotulo: '', exige_observacao: false}])}>
                                    <CIcon name="cil-plus"/> opção
                                </CButton>
                            )}
                        </div>
                    )}

                    <Marcar rotulo="Exige anexo" valor={item.exige_anexo || item.tipo === TipoItem.ARQUIVO}
                            desligado={ro || item.tipo === TipoItem.ARQUIVO} onMudar={v => mudaItem('exige_anexo', v)}/>
                    <Marcar rotulo="Permite declinar (com justificativa)" valor={item.permite_declinar} desligado={ro} onMudar={v => mudaItem('permite_declinar', v)}/>
                    <Marcar rotulo="Visível ao cliente" valor={item.visivel_cliente} desligado={ro} onMudar={v => mudaItem('visivel_cliente', v)}/>
                    <Campo rotulo="Pedido ao cliente" ajuda="O que o cliente precisa entregar para este item avançar. Aparece nas pendências do cliente.">
                        <CTextarea rows={2} maxLength={300} value={item.acao_cliente || ''} disabled={ro}
                                   placeholder="ex.: Enviar a planilha atual de produtos."
                                   onChange={(e: any) => mudaItem('acao_cliente', e.target.value)}/>
                    </Campo>
                    <Marcar rotulo="Ativo (inativo não entra no processo)" valor={item.ativo} desligado={ro} onMudar={v => mudaItem('ativo', v)}/>

                    {!ro && (
                        <div className="fx-painel-rodape">
                            <CButton size="sm" color="primary" variant="outline" onClick={duplicaItem}>
                                <CIcon name="cil-copy"/> Duplicar
                            </CButton>
                            <CButton size="sm" color="danger" variant="outline" onClick={removeItem}>
                                <CIcon name="cil-trash"/> Excluir item
                            </CButton>
                        </div>
                    )}
                </div>
            )}
        </aside>
    );
};

export default Painel;
